/**
 * Mock Auth Provider + unified useAppAuth hook.
 *
 * When VITE_MOCK_AUTH=true, provides a fake authenticated user.
 * When false, delegates to react-oidc-context.
 *
 * Switch mock user via VITE_MOCK_USER env var: 'admin' (default), 'staff', 'student'
 */

import { createContext, useContext, type ReactNode } from 'react';
import { useAuth as useOidcAuth } from 'react-oidc-context';

const isMockAuth = import.meta.env.VITE_MOCK_AUTH === 'true';
const mockUserKey = import.meta.env.VITE_MOCK_USER || 'admin';

export type UserGroup = 'student' | 'staff' | 'admin';

interface MockProfile {
  sub: string;
  email: string;
  email_verified: boolean;
  given_name: string;
  family_name: string;
  'custom:studentId'?: string;
  'custom:customerId': string;
  'custom:entityId': string;
  'custom:groups': UserGroup[];
  'cognito:groups'?: string[];
  [key: string]: unknown;
}

interface MockUser {
  profile: MockProfile;
  id_token: string;
  access_token: string;
  token_type: 'Bearer';
  expired: boolean;
  expires_in: number;
}

const MOCK_USERS: Record<string, MockUser> = {
  admin: {
    profile: {
      sub: 'mock-user-001',
      email: 'dev@example.com',
      email_verified: true,
      given_name: 'Dev',
      family_name: 'User',
      'custom:customerId': 'CUST-001',
      'custom:entityId': 'entity-1',
      'custom:groups': ['admin'],
    },
    id_token: 'mock-id-token-admin',
    access_token: 'mock-access-token-admin',
    token_type: 'Bearer',
    expired: false,
    expires_in: 3600,
  },
  staff: {
    profile: {
      sub: 'mock-user-002',
      email: 'staff@example.com',
      email_verified: true,
      given_name: 'Admin',
      family_name: 'Staff',
      'custom:customerId': 'CUST-002',
      'custom:entityId': 'entity-1',
      'custom:groups': ['staff'],
    },
    id_token: 'mock-id-token-staff',
    access_token: 'mock-access-token-staff',
    token_type: 'Bearer',
    expired: false,
    expires_in: 3600,
  },
  student: {
    profile: {
      sub: 'mock-user-003',
      email: 'student@example.com',
      email_verified: true,
      given_name: 'Jane',
      family_name: 'Doe',
      'custom:studentId': 'STU-11111',
      'custom:customerId': 'CUST-003',
      'custom:entityId': 'entity-2',
      'custom:groups': ['student'],
    },
    id_token: 'mock-id-token-student',
    access_token: 'mock-access-token-student',
    token_type: 'Bearer',
    expired: false,
    expires_in: 3600,
  },
};

const MOCK_USER = MOCK_USERS[mockUserKey] ?? MOCK_USERS.admin;

export interface AppAuth {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: Error | undefined;
  user: MockUser | null;
  signinRedirect: () => Promise<void>;
  signoutRedirect: () => Promise<void>;
}

const mockAuthValue: AppAuth = {
  isAuthenticated: true,
  isLoading: false,
  error: undefined,
  user: MOCK_USER,
  signinRedirect: async () => { console.log('[MockAuth] signinRedirect — no-op'); },
  signoutRedirect: async () => { console.log('[MockAuth] signoutRedirect — reloading'); window.location.reload(); },
};

const MockAuthContext = createContext<AppAuth>(mockAuthValue);

export function MockAuthProvider({ children }: { children: ReactNode }) {
  return (
    <MockAuthContext.Provider value={mockAuthValue}>
      {children}
    </MockAuthContext.Provider>
  );
}

/**
 * Unified auth hook — use this everywhere instead of useAuth().
 * Returns mock auth when VITE_MOCK_AUTH=true, real OIDC auth otherwise.
 */
export function useAppAuth(): AppAuth {
  if (isMockAuth) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useContext(MockAuthContext);
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const auth = useOidcAuth();
  console.log(auth.user);
  return auth as unknown as AppAuth;
}

/**
 * Get the current user's groups, normalised to lowercase.
 * Handles both mock auth (`custom:groups`: ['admin']) and real Cognito
 * (`cognito:groups`: ['Administrators']).
 */
export function useUserGroups(): UserGroup[] {
  const auth = useAppAuth();
  const profile = auth.user?.profile;
  if (!profile) return [];

  // Mock auth uses custom:groups; real Cognito uses cognito:groups
  const raw =
    (profile['custom:groups'] as string[] | undefined) ??
    (profile['cognito:groups'] as string[] | undefined) ??
    [];

  // Normalise Cognito group names → internal UserGroup values
  const COGNITO_TO_GROUP: Record<string, UserGroup> = {
    students: 'student',
    staff: 'staff',
    administrators: 'admin',
    // Also accept the mock values as-is
    student: 'student',
    admin: 'admin',
  };

  return raw.map((g) => COGNITO_TO_GROUP[g.toLowerCase()] ?? (g.toLowerCase() as UserGroup)).filter(Boolean);
}

export function useIsAdmin(): boolean {
  const groups = useUserGroups();
  return groups.includes('admin');
}

export function useIsStaff(): boolean {
  const groups = useUserGroups();
  return groups.includes('staff');
}

export function useIsAdminOrStaff(): boolean {
  const groups = useUserGroups();
  return groups.includes('admin') || groups.includes('staff');
}

export function useIsStudentOnly(): boolean {
  const groups = useUserGroups();
  return groups.length === 1 && groups[0] === 'student';
}
